/**
 * Where the consent gate applies (GitLab #649, D11 and §4).
 *
 * Two separate questions, deliberately kept apart:
 *
 *   - **Is this a customer surface?** Only customer-facing routes gate.
 *     Admin and team surfaces carry no GTM container and no gate (D11), so
 *     an internal user is never interrupted by a cookie dialogue.
 *   - **Must the gate stay out of the way here?** A few customer routes are
 *     mid-journey hand-offs where a blocking dialogue — or the reload that
 *     follows a choice — would break something the customer cannot recover.
 *
 * Suppression only hides the dialogue. Consent Mode defaults still apply and
 * no tag fires without a stored choice, so a suppressed path is strictly
 * safer than a gated one, never more permissive. The customer is asked on
 * the next ordinary page instead.
 */

/**
 * Customer-facing route prefixes.
 *
 * `/l` is absent on purpose: `app/l/[code]/route.ts` is a redirect handler
 * that renders no HTML, so there is nothing there to gate. It lands the
 * customer on `/r/...` or `/session/...`, both of which are listed.
 */
export const CONSENT_GATE_PATH_PREFIXES = [
  '/customer',
  '/session',
  '/r',
] as const;

/**
 * Paths where the dialogue must not appear.
 *
 * `/api/don` is the open-banking hand-off. The customer leaves for their
 * bank and comes back through the callback, which depends on a short-lived
 * `don_referral_id` cookie; a reload there loses the round-trip and the
 * customer has to start the bank journey again (§4.4).
 *
 * `/remote-auth` is the same shape of problem — a one-time token exchanged
 * on load, which a reload would spend twice.
 *
 * `/customer/credit-report` is where that remote-auth token lands. The
 * TransUnion identity journey runs inside the page rather than at the far end
 * of a redirect, so a reload mid-KBA restarts an identity check the customer
 * cannot simply retry.
 */
export const CONSENT_GATE_SUPPRESSED_PREFIXES = [
  '/api',
  '/remote-auth',
  '/auth',
  '/error',
  '/customer/credit-report',
] as const;

/**
 * Prefix match on whole path segments, so `/reports` is not treated as a
 * match for `/r`. Query strings and fragments never reach `usePathname()`,
 * but they are trimmed anyway for callers passing a raw URL.
 */
function matchesPrefix(pathname: string, prefix: string): boolean {
  const path = normalisePathname(pathname);
  return path === prefix || path.startsWith(`${prefix}/`);
}

/** Strip the query and fragment, drop a trailing slash, default to `/`. */
export function normalisePathname(pathname: string): string {
  const path = (pathname || '/').split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path || '/';
}

/** Whether this route is a customer surface at all. */
export function isCustomerSurface(pathname: string): boolean {
  return CONSENT_GATE_PATH_PREFIXES.some((prefix) =>
    matchesPrefix(pathname, prefix),
  );
}

/** Whether the dialogue must be held back on this route. */
export function isConsentGateSuppressed(pathname: string): boolean {
  return CONSENT_GATE_SUPPRESSED_PREFIXES.some((prefix) =>
    matchesPrefix(pathname, prefix),
  );
}

/**
 * Whether the blocking gate should be shown on this route. Suppression wins
 * over inclusion, so adding a customer prefix can never accidentally re-gate
 * a hand-off path.
 */
export function shouldGateConsent(pathname: string): boolean {
  return isCustomerSurface(pathname) && !isConsentGateSuppressed(pathname);
}
